/**
 * useIsStandalone — detects whether the app is running as an installed
 * PWA / TWA (display-mode: standalone) vs in a regular browser tab.
 *
 * Locked 2026-05-30 — Portal Role Doctrine + owner directive
 *  "if you download it as an app that's when it allows you to switch between
 *   the two and it should be seamless switching between the two."
 *
 * The hook re-evaluates whenever the user installs/uninstalls (the
 * matchMedia listener fires on display-mode change).
 */
import { useEffect, useState } from 'react';

const queryStandalone = () => {
  if (typeof window === 'undefined') return false;
  // iOS Safari sets navigator.standalone when installed to home screen.
  if (window.navigator && window.navigator.standalone) return true;
  if (window.matchMedia) {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
    if (window.matchMedia('(display-mode: minimal-ui)').matches) return true;
  }
  return false;
};

const useIsStandalone = () => {
  const [isStandalone, setIsStandalone] = useState(queryStandalone);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(display-mode: standalone)');
    const handler = () => setIsStandalone(queryStandalone());
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  return isStandalone;
};

export default useIsStandalone;
